/**
 * FRONT-2036 fix round 1 (items 14 & 16) — the small, pure display helpers a
 * research citation/run needs to render, shared across every surface that
 * shows one: the desktop banner (`chat-research-banner.tsx`), the desktop
 * briefing's Sources accordion (`sources-block.tsx`), and the phone's live
 * panel + briefing reader (`pwa-research-panel.tsx`, `pwa-research-strip.tsx`,
 * `pwa-briefing-reader.tsx`). No React, no icons, no query hooks — same
 * dependency-free-leaf precedent as `research-briefing.ts`/`plan-status.ts`/
 * `image-source.ts`.
 *
 * Before this file existed, `formatElapsed` and `hostOf` were each
 * independently copy-pasted into `chat-research-banner.tsx` AND (in this same
 * ticket) `pwa-research-logic.ts`/`pwa-briefing-reader.tsx` — three copies of
 * the identical few lines, justified at the time as "avoids pulling the
 * desktop banner's icons/mutation-hook imports onto the phone bundle." That
 * reasoning only ever applied to the desktop *component* module
 * (`chat-research-banner.tsx`), never to a plain pure-function file — so the
 * fix is what `research-briefing.ts` already established for the briefing
 * detector: one dependency-free copy, imported by every surface, never the
 * component module. `chat-research-banner.tsx` now imports from here too
 * (and re-exports `formatElapsed` so its own existing call sites/tests are
 * unaffected) rather than defining its own copy.
 */

/**
 * How many whole seconds/minutes/hours have elapsed, as "2m 14s" / "1h 05m" /
 * "38s". Never negative — a `startedAt` a beat ahead of this client's clock
 * clamps to "0s" rather than printing a negative duration. A non-finite
 * input (an unparseable `startedAt`, `new Date(x).getTime()` returning
 * `NaN`) returns the empty string rather than "NaNs" — callers already treat
 * that as "no elapsed readout" the same way they treat an absent
 * `startedAt`.
 */
export function formatElapsed(ms: number): string {
  if (!Number.isFinite(ms)) return '';
  const totalSeconds = Math.max(0, Math.floor(ms / 1000));
  const hours = Math.floor(totalSeconds / 3600);
  const minutes = Math.floor((totalSeconds % 3600) / 60);
  const seconds = totalSeconds % 60;
  if (hours > 0) return `${hours}h ${String(minutes).padStart(2, '0')}m`;
  if (minutes > 0) return `${minutes}m ${String(seconds).padStart(2, '0')}s`;
  return `${seconds}s`;
}

/** Bare hostname for a source row, falling back to the raw URL on a
 *  malformed one — so a citation reads the same host whether it's shown in
 *  the desktop banner, the desktop briefing's Sources accordion, or the
 *  phone's live panel/Sources sheet. */
export function hostOf(url: string): string {
  try {
    return new URL(url).hostname.replace(/^www\./, '');
  } catch {
    return url;
  }
}

/**
 * FRONT-2036 fix round 1 (item 16, hardening): a citation's `url` is
 * MODEL-authored — the research engine records whatever the search
 * provider handed back, and nothing upstream constrains its scheme. Both
 * the desktop Sources accordion and the phone's Sources sheet rendered it
 * straight into an `<a href>`, so a citation carrying a `javascript:` (or
 * other non-http(s)) URL would execute in the app's own origin the moment
 * someone tapped it — the exact class of bug the app's own Markdown
 * sanitizer (`markdown-renderer.tsx`, `markdown-inline.ts`) exists to
 * prevent for model-authored *content*; a model-authored *link* needs the
 * same discipline.
 *
 * Returns the URL unchanged when its scheme is `http:`/`https:`, else
 * `undefined` — callers render the citation as plain, unlinked text (title
 * + snippet, no `<a>`) rather than dropping the citation entirely, so an
 * odd source still shows up as a source, just not a clickable one.
 */
export function safeSourceHref(url: string): string | undefined {
  try {
    const parsed = new URL(url);
    return parsed.protocol === 'http:' || parsed.protocol === 'https:' ? url : undefined;
  } catch {
    return undefined;
  }
}
